import React, { useContext, useEffect, useState, useMemo } from 'react';
import DraggablePopover from 'spw-components/dialogs/draggable-popover';
import styled from 'styled-components';
import { Box } from '@mui/material';
import {
  SPWButtonIconAndText,
  SPWMainButton,
} from 'spw-shared/globalComponents/buttons';
import { OutlinedCardIcon } from 'spw-esa-projects/program-summary/outlined-card-icon';
import { SPWOutlineBlackButton } from 'spw-shared/globalComponents';
import { ProgramSummaryCardIcon } from 'spw-redux/modules/projects/types';
import { useDispatch, useSelector } from 'react-redux';
import { updateProgramSummaryCardIcon } from 'spw-redux/modules/program-summary/actions';
import RestoreIcon from 'spw-static/images/restore.svg';
import RestoreGrayIcon from 'spw-static/images/restore-gray.svg';
import { ProgramSummaryCardsContext } from 'spw-esa-projects/program-summary/summary-cards';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { currentProject as selectCurrentProject } from 'spw-redux/modules/projects/selectors';
import { EXISTING_SITE_ANALYSIS, PROGRAMMING } from 'spw-constants';
import { ProgrammingSummaryCardsContext } from 'spw-programming-projects/program-summary/program-summary-tabs/summary-card-context';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { isMacro } from 'spw-components/worksheet/utils';
import { PlainProgramScenario } from '../../../models/Scenario';

const Header = styled.div`
  padding: 16px 24px;
  cursor: grab;
`;

const Title = styled(Box)`
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
  padding: 0 24px 16px;
`;

const Content = styled.div`
  padding: 0 24px 16px 24px;
`;

const IconGrid = styled.div`
  && {
    display: flex;
    flex-direction: row;
    row-gap: 8px;
    column-gap: 8px;
    flex-wrap: wrap;
  }
`;

const Footer = styled.div`
  padding: 24px 24px;
  display: flex;
  flex-direction: row;
`;

const CancelButton = styled(SPWOutlineBlackButton)`
  margin-left: 13px;
  width: 80px;
`;

export enum ProgramSummaryCardType {
  // SHARED
  headcountUsf = 'HEADCOUNT_USF',
  seatCountUsf = 'SEAT_COUNT_USF',
  meetingSeatRatio = 'MEETING_SEAT_RATIO',
  // ESA ONLY
  deskSharingRatio = 'DESK_SHARING_RATIO',
  seatCountUsfRsfWorkpoints = 'SEAT_COUNT_USF_RSF_WORKPOINTS',
  deskSharingRatioWorkpoints = 'DESK_SHARING_RATIO_WORKPOINTS',
  // PROGRAMMING ONLY
  headcountAnalysis = 'HEADCOUNT_ANALYSIS',
  seatCountAnalysis = 'SEAT_COUNT_ANALYSIS',
}

type Props = {
  anchorEl: HTMLElement;
  cardIcon: ProgramSummaryCardIcon;
  onClose: () => void;
};

const HEADCOUNT_RELATED_ICONS = [
  'headcount-default',
  'headcount-2',
  'headcount-3',
  'headcount-4',
  'headcount-5',
  'custom-private-office-default',
  'private-office-default',
  'iw-custom-subcategory-default',
  'executive-private-office-default',
  'hotel-desk-default',
  'workstation-default',
  'meeting-chair',
  'workstation-3',
  'iw-category-default',
  'iw-category-3',
  'iw-category-2',
];

const SEAT_COUNT_RELATED_ICONS = [
  'meeting-chair',
  'desk-chair',
  'workstation-default',
  'custom-workstation-default',
  'custom-private-office-default',
  'private-office-default',
  'iw-custom-subcategory-default',
  'workstation-2',
  'shared-private-office-default',
  'executive-private-office-default',
  'shared-private-office-2',
  'hotel-desk-default',
  'workstation-3',
  'iw-category-default',
  'iw-category-3',
  'iw-category-2',
];

const CONTENT_WIDTH = 348;
const SMALL_ICON_WIDTH = 32;

const OFFSETS: Record<
  ProgramSummaryCardType,
  (params: {
    projectType?: string;
    scenario?: PlainProgramScenario;
    showRsf?: boolean;
  }) => { x: number; y: number }
> = {
  [ProgramSummaryCardType.headcountUsf]: (params) => {
    const { projectType, scenario } = params;
    if (projectType === PROGRAMMING) {
      if (isMacro(scenario)) {
        return { x: -(CONTENT_WIDTH + SMALL_ICON_WIDTH), y: 0 };
      }
      return {
        x: -(CONTENT_WIDTH + SMALL_ICON_WIDTH),
        y: 0,
      };
    }
    return { x: 0, y: 0 };
  },
  [ProgramSummaryCardType.seatCountUsf]: (params) => {
    const { projectType } = params;
    if (projectType === PROGRAMMING) {
      return {
        x: -(CONTENT_WIDTH + SMALL_ICON_WIDTH),
        y: 0,
      };
    }
    return { x: 0, y: 0 };
  },
  [ProgramSummaryCardType.headcountAnalysis]: (params) => {
    const { projectType } = params;
    if (projectType) {
      return { x: -(CONTENT_WIDTH + 40), y: 0 };
    }

    return { x: 0, y: 0 };
  },
  [ProgramSummaryCardType.seatCountAnalysis]: (params) => {
    const { projectType } = params;
    if (projectType === PROGRAMMING) {
      return { x: -(CONTENT_WIDTH + 60), y: 0 };
    }
    return { x: 0, y: 0 };
  },
  [ProgramSummaryCardType.meetingSeatRatio]: (params) => {
    const { projectType } = params;
    if (projectType === PROGRAMMING) {
      return { x: -(CONTENT_WIDTH + 48), y: 0 };
    }
    return { x: -(CONTENT_WIDTH + 54), y: 0 };
  },
  [ProgramSummaryCardType.deskSharingRatio]: () => ({ x: 0, y: 0 }),
  [ProgramSummaryCardType.seatCountUsfRsfWorkpoints]: (params) => {
    const { showRsf } = params;
    if (showRsf) {
      return { x: 8, y: 0 };
    }

    return { x: 0, y: 0 };
  },
  [ProgramSummaryCardType.deskSharingRatioWorkpoints]: () => ({ x: 0, y: 0 }),
};
const CARD_ICONS: Record<
  ProgramSummaryCardType,
  {
    cardTitle: string;
    defaultIcon: string;
    icons: string[];
  }
> = {
  [ProgramSummaryCardType.headcountUsf]: {
    cardTitle: 'Headcount USF',
    defaultIcon: 'headcount-default',
    icons: HEADCOUNT_RELATED_ICONS,
  },
  [ProgramSummaryCardType.seatCountUsf]: {
    cardTitle: 'Seat Count USF',
    defaultIcon: 'meeting-chair',
    icons: [
      'meeting-chair',
      'desk-chair',
      'workstation-default',
      'custom-workstation-default',
      'custom-private-office-default',
      'private-office-default',
      'iw-custom-subcategory-default',
      'workstation-2',
      'shared-private-office-default',
      'executive-private-office-default',
      'shared-private-office-2',
      'hotel-desk-default',
      'workstation-3',
      'iw-category-default',
      'iw-category-3',
      'iw-category-2',
    ],
  },
  [ProgramSummaryCardType.deskSharingRatio]: {
    cardTitle: 'Desk Sharing',
    defaultIcon: 'workstation-default',
    icons: [
      'meeting-chair',
      'desk-chair',
      'workstation-default',
      'custom-workstation-default',
      'custom-private-office-default',
      'private-office-default',
      'iw-custom-subcategory-default',
      'workstation-2',
      'shared-private-office-default',
      'executive-private-office-default',
      'shared-private-office-2',
      'hotel-desk-default',
      'workstation-3',
      'iw-category-default',
      'iw-category-3',
      'iw-category-2',
    ],
  },
  [ProgramSummaryCardType.meetingSeatRatio]: {
    cardTitle: 'Meeting Seat Ratio',
    defaultIcon: 'meeting-chair',
    icons: [
      'meeting-chair',
      'meeting-2',
      'meeting-3',
      'phone-default',
      'shared-private-office-default',
      'executive-private-office-default',
      'shared-private-office-2',
      'workstation-3',
      'people',
      'huddle-default',
      'sm-conf-default',
      'meeting-category-default',
      'med-conf-default',
      'lg-conf-default',
      'xl-conf-default',
      'multi-purpose-default',
    ],
  },
  [ProgramSummaryCardType.seatCountUsfRsfWorkpoints]: {
    cardTitle: 'Seat Count USF',
    defaultIcon: 'meeting-chair',
    icons: SEAT_COUNT_RELATED_ICONS,
  },
  [ProgramSummaryCardType.deskSharingRatioWorkpoints]: {
    cardTitle: 'Desk Sharing',
    defaultIcon: 'workstation-default',
    icons: [
      'meeting-chair',
      'desk-chair',
      'workstation-default',
      'custom-workstation-default',
      'custom-private-office-default',
      'private-office-default',
      'iw-custom-subcategory-default',
      'workstation-2',
      'shared-private-office-default',
      'executive-private-office-default',
      'shared-private-office-2',
      'hotel-desk-default',
      'workstation-3',
      'iw-category-default',
      'iw-category-3',
      'iw-category-2',
    ],
  },
  [ProgramSummaryCardType.headcountAnalysis]: {
    cardTitle: 'Headcount Analysis',
    defaultIcon: 'headcount-default',
    icons: HEADCOUNT_RELATED_ICONS,
  },
  [ProgramSummaryCardType.seatCountAnalysis]: {
    cardTitle: 'Seat Count Analysis',
    defaultIcon: 'meeting-chair',
    icons: SEAT_COUNT_RELATED_ICONS,
  },
};

const Disclaimer = styled.div`
  && {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }
`;

const IconSelectorPopover: React.FC<Props> = ({
  anchorEl,
  cardIcon,
  onClose,
}) => {
  const dispatch = useDispatch();
  const project = useSelector(selectCurrentProject);
  const scenario = useSelector(selectCurrentScenario);

  const { showRsf } =
    project.type === EXISTING_SITE_ANALYSIS
      ? useContext(ProgramSummaryCardsContext)
      : useContext(ProgrammingSummaryCardsContext);
  const [selectedIcon, setSelectedIcon] = useState<string>();
  const { getUnit } = useUnitConversion();

  const updateCardIcon = () => {
    dispatch(
      updateProgramSummaryCardIcon({
        cardType: cardIcon.cardType,
        iconPath: selectedIcon,
      })
    );

    onClose();
    setSelectedIcon(null);
  };

  useEffect(() => {
    setSelectedIcon(cardIcon?.iconPath);
  }, [cardIcon?.iconPath]);

  const handleOnRestore = () => {
    const defaultIcon = CARD_ICONS[cardIcon?.cardType]?.defaultIcon;
    setSelectedIcon(defaultIcon);
  };

  const handleOnCancel = () => {
    onClose();
    setSelectedIcon(null);
  };

  const isResetDisabled =
    CARD_ICONS[cardIcon?.cardType]?.defaultIcon === selectedIcon;

  const cardTitle = useMemo(() => {
    let title = CARD_ICONS[cardIcon?.cardType]?.cardTitle;

    if (
      [
        ProgramSummaryCardType.headcountUsf,
        ProgramSummaryCardType.seatCountUsf,
        ProgramSummaryCardType.seatCountUsfRsfWorkpoints,
      ].includes(cardIcon?.cardType)
    ) {
      const unit = getUnit('area');
      const rentableAreaUnit = getUnit('rentable-area');
      title = title.replace('USF', unit);

      if (
        showRsf &&
        [
          ProgramSummaryCardType.headcountUsf,
          ProgramSummaryCardType.seatCountUsf,
        ].includes(cardIcon?.cardType) &&
        project.type === PROGRAMMING
      ) {
        title = title.replace(unit, `${unit} + ${rentableAreaUnit}`);
      } else if (showRsf) {
        title = title.replace(unit, `${rentableAreaUnit} + ${unit}`);
      }
    }

    return title;
  }, [cardIcon?.cardType, showRsf, project.type, getUnit]);

  const offset = useMemo(() => {
    const offsetFunction = OFFSETS[cardIcon?.cardType];
    if (offsetFunction) {
      return offsetFunction({ projectType: project.type, scenario, showRsf });
    }

    return { x: 0, y: 0 };
  }, [cardIcon?.cardType, showRsf, project.type, scenario]);

  if (!cardIcon) return null;

  return (
    <DraggablePopover
      anchorEl={anchorEl}
      onClose={() => {
        onClose();
      }}
      paperProps={{ style: { width: CONTENT_WIDTH } }}
      handle=".handle-drag"
      offset={offset}
    >
      <Header className="handle-drag">
        <Disclaimer>
          Changing an icon will update everywhere in this project.
        </Disclaimer>
      </Header>
      <Title>{cardTitle}</Title>
      <Content>
        <IconGrid>
          {CARD_ICONS[cardIcon.cardType]?.icons?.map((iconPath) => {
            return (
              <OutlinedCardIcon
                key={iconPath}
                iconPath={iconPath}
                disabled={cardIcon.iconPath === iconPath}
                isSelected={
                  selectedIcon !== cardIcon.iconPath &&
                  iconPath === selectedIcon
                }
                onClick={() => setSelectedIcon(iconPath)}
              />
            );
          })}
        </IconGrid>
      </Content>
      <Footer>
        <SPWMainButton
          style={{ width: 80 }}
          disabled={cardIcon?.iconPath === selectedIcon}
          onClick={updateCardIcon}
        >
          Save
        </SPWMainButton>
        <CancelButton onClick={handleOnCancel}>Cancel</CancelButton>
        <SPWButtonIconAndText
          style={{ marginLeft: 'auto' }}
          startIcon={
            <img
              src={isResetDisabled ? RestoreGrayIcon : RestoreIcon}
              alt="restore"
            />
          }
          disabled={
            CARD_ICONS[cardIcon?.cardType]?.defaultIcon === selectedIcon
          }
          onClick={handleOnRestore}
        >
          Restore
        </SPWButtonIconAndText>
      </Footer>
    </DraggablePopover>
  );
};

export default IconSelectorPopover;
